import { type OpportunityStatusEnum, StagesEnum } from "@repo/api";
import { useSettings } from "@repo/ui";
import { useMemo } from "react";

import { getStageActionsFromSettings, getStagesFromSettings } from "./config";
import { isClosedLostOrInvalidStatus } from "./utils";

interface UseStageNavigationStateParams {
  currentStage: StagesEnum;
  currentStatus: OpportunityStatusEnum | undefined;
}

export const useStageNavigationState = ({
  currentStage,
  currentStatus,
}: UseStageNavigationStateParams) => {
  const { project_stages } = useSettings();
  const isClosedLostOrInvalid = isClosedLostOrInvalidStatus(currentStatus);

  const stages = useMemo(() => {
    const configuredStages = getStagesFromSettings(project_stages);

    if (!isClosedLostOrInvalid) {
      return configuredStages;
    }

    const salesClosedIndex = configuredStages.indexOf(StagesEnum.SALES_CLOSED);
    const currentStageIndex = configuredStages.indexOf(currentStage);
    const shouldHideLaterStages =
      salesClosedIndex >= 0 &&
      currentStageIndex >= 0 &&
      currentStageIndex >= salesClosedIndex;

    if (!shouldHideLaterStages) {
      return configuredStages;
    }

    return configuredStages.slice(0, salesClosedIndex + 1);
  }, [currentStage, isClosedLostOrInvalid, project_stages]);

  const stageActions = useMemo(
    () => getStageActionsFromSettings(project_stages),
    [project_stages]
  );

  return useMemo(() => {
    const currentStep = stages.indexOf(currentStage) + 1 || 1;
    const salesClosedStep = stages.indexOf(StagesEnum.SALES_CLOSED) + 1;
    const length = stages.length;
    const hasStages = length > 0;

    const isAfterOrAtSalesClosed =
      salesClosedStep > 0 && currentStep >= salesClosedStep;
    const canGoPrevious =
      hasStages && currentStep > 1 && stages[currentStep - 2] !== undefined;
    const canGoNextBase =
      hasStages && currentStep < length && stages[currentStep] !== undefined;
    const canGoNext =
      canGoNextBase && !(isClosedLostOrInvalid && isAfterOrAtSalesClosed);
    const nextTargetStage = canGoNext ? stages[currentStep] : undefined;
    const shouldOpenClosureModal = nextTargetStage === StagesEnum.SALES_CLOSED;
    const currentStageActions =
      hasStages && stages[currentStep - 1]
        ? stageActions[stages[currentStep - 1]] || []
        : [];

    return {
      stages,
      currentStep,
      salesClosedStep,
      length,
      canGoPrevious,
      canGoNext,
      nextTargetStage,
      shouldOpenClosureModal,
      currentStageActions,
    };
  }, [currentStage, isClosedLostOrInvalid, stageActions, stages]);
};
